import type { ReactNode } from 'react';
import React, { useEffect, useCallback, forwardRef, useRef } from 'react';
import { css, cssMap } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';

import { useHomeWidgetState } from './useHomeWidgetState.ts';
import { HomeWidgetState } from './__types__/HomeWidgetsQuery.ts';

type CollapsibleSectionProps = {
	sectionId: string;
	header: ReactNode;
	headerActions?: ReactNode; // absolute positioned to avoid nesting buttons in header for better accessibility
	defaultState?: HomeWidgetState;
	initialStateOverride?: HomeWidgetState;
	onInitialCollapse?: () => void;
	spotlightName?: string;
	ariaLabel?: string;
	children: ReactNode;
};

const collapsibleStyles = css({
	backgroundColor: token('elevation.surface.raised'),
	position: 'relative',
	marginBottom: token('space.150'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
});

const collapsibleCollapsedStyles = css({
	'&:hover': {
		boxShadow: token('elevation.shadow.raised'),
	},
});

const headerExpandCollapseButtonStyles = cssMap({
	default: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
		backgroundColor: token('elevation.surface.raised'),
		font: token('font.body'),
		padding: token('space.100'),
		margin: 0,
		position: 'sticky',
		top: token('space.negative.150'),
		zIndex: 1,
		border: 'none',
		borderTopLeftRadius: token('radius.large'),
		borderTopRightRadius: token('radius.large'),
		'&:hover': {
			cursor: 'pointer',
		},
	},
	collapsed: {
		borderRadius: token('radius.large'),
	},
	expanded: {
		borderBottom: 'none',
	},
});

const headerActionsWrapperStyles = css({
	position: 'absolute',
	right: token('space.400'),
	top: token('space.100'),
	zIndex: 1,
});

const collasibleContentStyles = css({
	padding: token('space.100'),
});

/**
 * CollapsibleSection should be used within the right rail on the home screen. All sections of the right rail should
 * be placed inside of a CollapsibleSection. Local storage and collapsed state will be handled within this component.
 *
 * By default the header will be displayed when the section is expanded, to hide the header use the prop
 * hideHeaderWhenExpanded. If hideHeaderWhenExpanded is false, the content of the section will occupy the full width
 * with 24px of left and right padding. If hidHeaderWhenExpanded is true the content will only occupy the space left
 * of the expand/collapse button with 24px of left padding and 40px of right padding.
 *
 * @param sectionId: Unique identifier for the section, used for local storage
 * @param header: React node to appear in the header. By default the header will be rendered in the expanded state
 * @param hideHeaderWhenExpanded: Boolean value that, when true, will hide the header when the section is expanded
 * @param children: React node to be rendered in the expanded state. Children must be styled appropriately.
 */
export const CollapsibleSection = forwardRef<HTMLDivElement, CollapsibleSectionProps>(
	(
		{
			sectionId,
			header,
			headerActions,
			children,
			defaultState,
			initialStateOverride,
			onInitialCollapse,
			spotlightName,
			ariaLabel,
		},
		ref,
	) => {
		const { widgetState, setWidgetState, loading, error } = useHomeWidgetState(
			sectionId,
			defaultState,
			initialStateOverride,
		);
		const isCollapsed = widgetState === HomeWidgetState.COLLAPSED;
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const checkedInitialState = useRef(false);
		useEffect(() => {
			if (loading || checkedInitialState.current) return;
			checkedInitialState.current = true;
			if (widgetState === HomeWidgetState.COLLAPSED) {
				onInitialCollapse?.();
			}
		}, [loading, widgetState, onInitialCollapse]);

		const fireAnalyticsEvent = useCallback(
			(action: 'collapsed' | 'expanded') => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action,
						actionSubject: 'collapsibleSection',
						actionSubjectId: sectionId,
						source: 'rightPanel',
						attributes: {
							homeVersion: '1.5',
						},
					},
				}).fire();
			},
			[createAnalyticsEvent, sectionId],
		);

		const onClick = useCallback(() => {
			const onCollapse = () => {
				setWidgetState(HomeWidgetState.COLLAPSED);
				fireAnalyticsEvent('collapsed');
			};

			const onExpand = () => {
				setWidgetState(HomeWidgetState.EXPANDED);
				fireAnalyticsEvent('expanded');
			};

			isCollapsed ? onExpand() : onCollapse();
		}, [isCollapsed, setWidgetState, fireAnalyticsEvent]);

		if (loading) {
			return null;
		}

		const Icon = isCollapsed ? ChevronRightIcon : ChevronDownIcon;

		const headerId = `home-right-panel-header-${sectionId}`;

		const sectionHeaderCompiled = (
			<>
				{/* eslint-disable-next-line @atlaskit/design-system/no-html-button */}
				<button
					id={headerId}
					onClick={onClick}
					aria-expanded={!isCollapsed}
					aria-controls="collapsible-content"
					aria-label={ariaLabel}
					css={[
						headerExpandCollapseButtonStyles['default'],
						isCollapsed && headerExpandCollapseButtonStyles['collapsed'],
						!isCollapsed && headerExpandCollapseButtonStyles['expanded'],
					]}
				>
					{header}
					<Icon label="" size="small" />
				</button>
				<span css={headerActionsWrapperStyles}>{headerActions}</span>
			</>
		);

		return (
			<div
				css={[collapsibleStyles, isCollapsed && collapsibleCollapsedStyles]}
				ref={ref}
				data-testid={sectionId}
			>
				{spotlightName ? (
					<SpotlightTarget name={spotlightName}>{sectionHeaderCompiled}</SpotlightTarget>
				) : (
					sectionHeaderCompiled
				)}
				{isCollapsed ? null : (
					<div
						css={collasibleContentStyles}
						id="collapsible-content"
						role="region"
						aria-labelledby={headerId}
					>
						{children}
					</div>
				)}
				{error && <ErrorDisplay error={error} />}
			</div>
		);
	},
);
